import { AlertTriangle, History, X } from "lucide-react";
import { domaineLabel, fmtDate, mediaUrl, statutLabel } from "@/lib/api";

export default function MaterielHistorique({ data, onClose }) {
  if (!data) return null;
  const { materiel, tickets } = data;

  return (
    <div className="fixed inset-0 z-[90] bg-black/60 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-6" data-testid="materiel-historique">
      <div className="bg-white w-full sm:max-w-2xl max-h-[88vh] rounded-t-2xl sm:rounded-2xl overflow-hidden flex flex-col">
        <div className="flex items-start gap-3 px-5 py-4 border-b border-slate-100">
          <History size={18} className="text-[#005CFF] mt-1 shrink-0" />
          <div className="min-w-0 flex-1">
            <div className="text-sm font-bold text-slate-900 truncate">{materiel.designation}</div>
            {data.a_risque && (
              <div
                data-testid="histo-badge-risque"
                className="inline-flex items-center gap-1.5 mt-1 rounded-full bg-red-50 border border-red-200 px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wider text-red-700"
              >
                <AlertTriangle size={11} /> Équipement à risque · {data.tickets_periode} pannes / {data.mois_risque} mois
              </div>
            )}
            <div className="text-xs text-slate-500 truncate">
              {domaineLabel(materiel.domaine)}
              {materiel.numero_serie ? ` · SN ${materiel.numero_serie}` : ""}
              {materiel.date_pose ? ` · posé le ${materiel.date_pose}` : ""}
            </div>
          </div>
          <button data-testid="histo-close-btn" onClick={onClose} className="p-2 rounded-full hover:bg-slate-100 text-slate-500">
            <X size={17} />
          </button>
        </div>

        <div className="px-5 py-4 grid grid-cols-3 gap-3 border-b border-slate-100">
          {[
            { label: "Tickets au total", value: data.total, testid: "histo-total" },
            { label: "Encore ouverts", value: data.ouverts, testid: "histo-ouverts" },
            { label: "Auto-résolus", value: data.auto_resolus, testid: "histo-auto" },
          ].map((s) => (
            <div key={s.label} data-testid={s.testid} className="rounded-xl bg-slate-50 px-3 py-3">
              <div className="font-display text-2xl font-black tracking-tighter text-slate-900">{s.value}</div>
              <div className="text-[11px] uppercase tracking-wider text-slate-500 mt-1">{s.label}</div>
            </div>
          ))}
        </div>

        {data.motifs_recurrents?.length > 0 && data.total > 1 && (
          <div className="px-5 py-3 border-b border-slate-100 bg-amber-50/60" data-testid="histo-motifs">
            <div className="flex items-center gap-2 text-xs font-semibold text-amber-800">
              <AlertTriangle size={13} /> Motifs les plus fréquents
            </div>
            <div className="text-sm text-slate-700 mt-1.5">
              {data.motifs_recurrents.map(([motif, n]) => `${motif} (${n})`).join(" · ")}
            </div>
          </div>
        )}

        <div className="overflow-y-auto px-5 py-4 space-y-3">
          {tickets.length === 0 && <p className="text-sm text-slate-500" data-testid="histo-vide">Aucun ticket enregistré sur cet équipement.</p>}
          {tickets.map((t) => (
            <div key={t.id} data-testid={`histo-ticket-${t.reference}`} className="rounded-xl border border-slate-200 px-4 py-3">
              <div className="flex flex-wrap items-center gap-2 justify-between">
                <span className="font-mono-bk text-xs text-slate-500">{t.reference}</span>
                <span className="text-xs font-semibold text-slate-700">{statutLabel(t.statut)}</span>
                <span className="text-xs text-slate-500">{fmtDate(t.created_at)}</span>
              </div>
              <div className="text-sm text-slate-900 mt-1">
                {(t.qcm_path?.[t.qcm_path.length - 1]?.reponse) || t.description_texte || "Demande sans description"}
              </div>
              {t.solution_appliquee && <div className="text-xs text-emerald-700 mt-1">Résolu par : {t.solution_appliquee}</div>}
              {t.photos_resolution?.length > 0 && (
                <div className="mt-2" data-testid={`histo-resolution-${t.reference}`}>
                  <div className="text-[11px] uppercase tracking-wider text-emerald-700 mb-1.5">Après intervention</div>
                  <div className="flex gap-2 flex-wrap">
                    {t.photos_resolution.map((p) => (
                      <a key={p.url} href={mediaUrl(p.url)} target="_blank" rel="noreferrer">
                        <img src={mediaUrl(p.url)} alt="Après intervention" className="h-16 w-20 object-cover rounded-lg border border-emerald-200" />
                      </a>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
